/**
 * The confirmation e-mail, ported — `emailPlaceholders` / `renderEmail` (`m03-state.js:466`) and
 * `emailHTML` (`m07-import.js:285`).
 *
 * Two things about this surface are easy to get wrong and both are load-bearing.
 *
 * The subject and body come from the credential's own immutable release (`emailDefaults`), NOT from
 * the four «Konu / Metin» boxes on the settings form. The reference's form writes those keys into
 * `settings.email` and the preview never reads them back, so a template edit changes the sender
 * block and nothing else. That is reproduced rather than corrected: a release is the single source
 * a credential is produced from (binding rule 3), and the seed's settings carry no such keys at all.
 *
 * A pending record's CTA may only ever be a REAL claim link (§6.4). When the raw token is not in
 * the session there is no silent fall back to the public URL — the public page of a pending record
 * shows no personal data (rule 12), so that link would lead somewhere empty.
 */
import type { DemoCredential, DemoDatabase } from '../data/types';
import { rawClaimToken } from './credential-register';
import { credTitle, displayName, fmtDate, nowUtcSeconds, publicUrl } from './reference';
import { credTerms } from './render';

/**
 * `prototypeNowUTC` — `m01-utils.js:105`, served by the view clock.
 *
 * In the demo the DATE is still the frozen prototype day and only the clock time moves, so a
 * record written during the demo sorts alongside the seed instead of a year after it. In the
 * product both halves are the real instant.
 */
export const prototypeNowUTC = nowUtcSeconds;

interface OutboxEntry {
  rawToken?: string;
  claimUrl?: string;
}

function outboxEntry(db: DemoDatabase, c: DemoCredential): OutboxEntry | null {
  const box = (db as { outbox?: unknown }).outbox;
  if (box === null || typeof box !== 'object') return null;
  const entry = (box as Record<string, unknown>)[c.id];
  return entry !== null && typeof entry === 'object' ? (entry as OutboxEntry) : null;
}

/** `claimUrlFor` — a structural answer, never an ellipsis that reads like a copyable link. */
export function claimUrlFor(db: DemoDatabase, c: DemoCredential): string | null {
  const o = outboxEntry(db, c);
  return o?.rawToken && o.claimUrl ? o.claimUrl : null;
}

const langOf = (c: DemoCredential): 'tr' | 'en' => (c.lang === 'en' ? 'en' : 'tr');

/** `{{...}}` substitution, with the reference's own «missing key becomes empty» rule. */
const fillPlaceholders = (tpl: unknown, vars: Record<string, string>): string =>
  String(tpl ?? '').replace(/\{\{(\w+)\}\}/g, (_, k: string) => vars[k] ?? '');

export function emailPlaceholders(db: DemoDatabase, c: DemoCredential): Record<string, string> {
  const lang = langOf(c);
  const d = (c as { renderDataSnapshot?: Record<string, unknown> }).renderDataSnapshot ?? {};
  const vars: Record<string, string> = {};
  for (const [k, v] of Object.entries(c.claims ?? {})) {
    if (v !== null && v !== undefined) vars[k] = String(v);
  }
  const issue = typeof d.issue_date === 'string' ? d.issue_date : '';
  const expiry = typeof d.expiry_date === 'string' ? d.expiry_date : '';
  vars.holder_name = displayName(c);
  vars.credential_title = credTitle(c, lang);
  vars.credential_code = c.publicCode;
  vars.credential_url = publicUrl(c);
  const claim = claimUrlFor(db, c);
  if (claim !== null) vars.claim_url = claim;
  vars.issue_date = issue || fmtDate(c.issueDate, lang);
  /* «Süresiz» in both languages — the reference hard-codes it and the placeholder list says so. */
  vars.expiry_date = expiry || (c.expiresAt ? fmtDate(c.expiresAt, lang) : 'Süresiz');
  vars.issuer_name = c.release.issuerProfileSnapshot.displayName;
  return vars;
}

export interface RenderedEmail {
  subject: string;
  body: string;
  heading: string;
  cta: string;
  ctaHash: string | null;
  ctaAvailable: boolean;
  to: string;
  from: string;
  lang: 'tr' | 'en';
}

export function renderEmail(db: DemoDatabase, c: DemoCredential): RenderedEmail {
  const lang = langOf(c);
  const vars = emailPlaceholders(db, c);
  const T = credTerms(c, lang);
  const ed = (c.release.emailDefaults ?? {}) as { subjectTemplate?: unknown; bodyTemplate?: unknown };
  const pending = c.state === 'pending_acceptance';
  const raw = rawClaimToken(db, c);
  const email = (db.settings.email ?? {}) as Record<string, unknown>;
  const str = (k: string): string => (typeof email[k] === 'string' ? (email[k] as string) : '');
  return {
    subject: fillPlaceholders(ed.subjectTemplate, vars),
    body: fillPlaceholders(ed.bodyTemplate, vars),
    heading: T.ready,
    cta: pending ? T.claimCta : lang === 'en' ? 'View my credential' : 'Kaydımı görüntüle',
    ctaHash: pending ? (raw ? `#/claim/${raw}` : null) : `#/c/${c.publicCode}`,
    ctaAvailable: pending ? raw !== null : true,
    to: c.privateContactEmail ?? '',
    from: `${str('senderName')} <${str('fromEmail')}>`,
    lang,
  };
}

/**
 * The body is emitted as one `<p>` per non-empty line. The reference's filter drops a blank line
 * that follows another blank line, which for the output is the same as dropping every blank line —
 * a blank line never produced a paragraph in the first place.
 */
export const emailBodyLines = (body: string): string[] => body.split('\n').filter((l) => l !== '');

export interface TestEmail {
  at: string;
  to: string;
  subject: string;
}

/** The seed carries `testEmails` untyped; the three fields the log line prints are narrowed here. */
export function testEmails(db: DemoDatabase): TestEmail[] {
  const list = (db as { testEmails?: unknown }).testEmails;
  if (!Array.isArray(list)) return [];
  return list.map((x) => {
    const r = (x ?? {}) as Record<string, unknown>;
    return {
      at: typeof r.at === 'string' ? r.at : '',
      to: typeof r.to === 'string' ? r.to : '',
      subject: typeof r.subject === 'string' ? r.subject : '',
    };
  });
}

export function pushTestEmail(db: DemoDatabase, entry: TestEmail): void {
  const list = (db as { testEmails?: unknown }).testEmails;
  const target = Array.isArray(list) ? (list as unknown[]) : [];
  if (!Array.isArray(list)) (db as { testEmails?: unknown }).testEmails = target;
  target.push({ ...entry, file: null, row: null });
}
